import React, { useEffect, useMemo, useState, useRef } from "react";
import {
    Form,
    Button,
    Alert,
    Spinner,
    Modal,
} from 'react-bootstrap';
import { FiSave, FiArrowLeft, FiBriefcase, FiGlobe, FiEye } from 'react-icons/fi';
import { createDepartmentAsync } from '@features/department/services/departmentSvc';
import { fetchNationsAsync } from '@features/nation/services/nationSvc';
import { Nation } from '@features/nation/types/nation';
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { ErrorResponse } from '@core/types/errorResponse';
import { useApiError } from '@infrastructure/api/hooks/useApiError';
import { UpdateDepartmentRequest } from '@features/department/types/departmentRequest';
import { useDepartmentActions } from '@features/department/hooks/useDepartmentActions';
import InfoTooltip from '@core/components/InfoTooltip';
import { useScopeId } from '@core/hooks/useRouteParamId';

// Define type for field errors
type FieldErrors = Partial<Record<keyof UpdateDepartmentRequest, string[]>>;

const UpdateDepartmentModal: React.FC<{ show: boolean; onHide: () => void; departmentId: number | null; onClearDepartmentId?: () => void; onUpdated?: () => void; readOnly?: boolean }> = ({ show, onHide, departmentId, onClearDepartmentId, onUpdated, readOnly = false }) => {
    const resolvedNationId = useScopeId() ?? 0;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formData, setForm] = useState<UpdateDepartmentRequest>({
        name: "",
        description: "",
    });
    const [formErrors, setFormErrors] = useState<FieldErrors>({});
    const showError = useApiError();
    const { busy, department, fetchDepartment, doDepartmentUpdate } = useDepartmentActions();

    // Initial form state
    const initialForm: UpdateDepartmentRequest = {
        name: "",
        description: "",
    };

    // Handle modal close
    const handleClose = () => {
        setForm(initialForm);
        setFormErrors({});
        setError(null);
        onHide();
        // if parent provided a callback to clear departmentId, call it
        onClearDepartmentId?.();
    };

    // Fetch department data when departmentId changes
    useEffect(() => {
        if (departmentId == null) return;

        // Fetch department info
        fetchDepartment(
            departmentId,
            resolvedNationId > 0 ? { nationId: resolvedNationId } : undefined
        ).then(department => {
            if (department) {
                const d = department.data[0];
                // Map Department -> UpdateDepartmentRequest, providing defaults for optional fields
                setForm({
                    name: d.name ?? "",
                    description: d.description ?? "",
                });
            }
        });
    }, [departmentId, fetchDepartment, resolvedNationId]);

    // Handle input changes
    const handleInputChange = (field: keyof UpdateDepartmentRequest) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        let value: string = e.target.value;

        // Update form state
        setForm(f => ({
            ...f,
            [field]: value
        }));
    };

    const submit = async () => {
        // Ensure departmentId is present
        if (departmentId == null) return;

        setFormErrors({});
        setError(null);

        try {
            // Perform department update
            const response = await doDepartmentUpdate(resolvedNationId, departmentId, formData);

            if (response.isSuccess()) {
                // Show success message
                showSuccess(response.getData()?.message);
                // Close modal and reset form
                onHide();
                setForm(initialForm);

                // Notify parent of update
                onUpdated?.();

            } else if (response.getError()?.errors) {
                // Set form errors if present
                setFormErrors(response.getError()!.errors as FieldErrors);
            }
        } catch (err) {
            // Handle unexpected errors
            showError(err as ErrorResponse);
            setError("Failed to update department. Please try again.");
        }
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            centered
            size="lg"
            data-testid="update-department-modal"
        >
            <Modal.Header closeButton className="border-0 pb-0" data-testid="update-department-title-wrap">
                <Modal.Title className="fw-semibold" data-testid="update-department-title">
                    {readOnly ? <FiEye className="me-2 text-primary" /> : <FiBriefcase className="me-2 text-primary" />}
                    {readOnly ? 'Department Details' : 'Update Department'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-3">
                <Form data-testid="update-department-form" noValidate>
                    <Form.Group controlId="departmentName" className="mb-3">
                        <Form.Label>Name<span className="text-danger">*</span><InfoTooltip text="The department name as it appears in the system and in reports." /></Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter department name"
                            value={formData.name}
                            onChange={handleInputChange("name")}
                            isInvalid={!!formErrors.name}
                            required
                            disabled={readOnly}
                            aria-label="Department name"
                            data-testid="department-name-input"
                        />
                        <Form.Control.Feedback
                            type="invalid"
                            role="alert"
                            data-testid="department-name-error"
                        >
                            {formErrors.name?.[0]}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group controlId="departmentDescription" className="mb-3">
                        <Form.Label>Description<InfoTooltip text="Optional short description of the department's purpose." /></Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            placeholder="Enter a short description"
                            value={formData.description}
                            onChange={handleInputChange("description")}
                            isInvalid={!!formErrors.description}
                            disabled={readOnly}
                            aria-label="Department description"
                            data-testid="department-description-input"
                        />
                        <Form.Control.Feedback
                            type="invalid"
                            role="alert"
                            data-testid="department-description-error"
                        >
                            {formErrors.description?.[0]}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Form>
                {!readOnly && (
                    <div className="d-flex justify-content-end mt-3">
                        <Button
                            variant="primary"
                            size="lg"
                            className="btn-action"
                            disabled={busy}
                            onClick={submit}
                            data-testid="update-department-submit"
                            aria-busy={busy}
                        >
                            {busy ? (
                                <>
                                    <Spinner
                                        animation="border"
                                        size="sm"
                                        className="me-2"
                                        role="status"
                                        aria-hidden="true"
                                        data-testid="update-department-spinner"
                                    />
                                    Updating...
                                </>
                            ) : (
                                <>Update Department</>
                            )}
                        </Button>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
}

export default UpdateDepartmentModal;